import api from "./baseApi";
import endpoints from "./endpoints";

/**
 * Get lots with pagination + optional filters
 */
/**
 * Get all lots
 */
export const getLots = async (params = {}) => {
  try {
    const {
      page = 1,
      perPage = 10,
      sortField,
      sortOrder,
      keyword,
      location,
      type,
      status,
      requiresPayment,
      requiresApproval,
      allowPublicBooking,
      capacity,
      minCapacity,
      maxCapacity,
      filters = {},
    } = params;

    const finalFilters = {
      ...filters,
      ...(keyword !== "" && keyword !== undefined ? { keyword } : {}),
      ...(location !== "" && location !== undefined ? { location } : {}),
      ...(type !== "" && type !== undefined ? { type } : {}),
      ...(status !== "" && status !== undefined ? { status } : {}),
      ...(requiresPayment !== "" && requiresPayment !== undefined
        ? { requiresPayment }
        : {}),
      ...(requiresApproval !== "" && requiresApproval !== undefined
        ? { requiresApproval }
        : {}),
      ...(allowPublicBooking !== "" && allowPublicBooking !== undefined
        ? { allowPublicBooking }
        : {}),
      ...(capacity !== "" && capacity !== undefined ? { capacity } : {}),
      ...(minCapacity !== "" && minCapacity !== undefined
        ? { minCapacity }
        : {}),
      ...(maxCapacity !== "" && maxCapacity !== undefined
        ? { maxCapacity }
        : {}),
    };

    const res = await api.post(endpoints.lot.base, {
      page,
      perPage,
      sortField,
      sortOrder,
      filters: finalFilters,
    });

    return res.data;
  } catch (err) {
    const message =
      err.response?.data?.message || "failed to fetch lots";

    throw new Error(message);
  }
};

/**
 * Get single lot by ID
 */
export const getLotById = async (id) => {
  try {
    const res = await api.get(endpoints.lot.detail(id));
    return res.data;
  } catch (err) {
    const message =
      err.response?.data?.message || "failed to fetch lot";
    throw new Error(message);
  }
};

/**
 * Create a new lot
 */
export const createLot = async (payload) => {
  try {
    const formData = new FormData();

    formData.append("bulkCreateNumber", payload.bulkCreateNumber);
    formData.append("name", payload.name);
    formData.append("location", payload.location || "");
    formData.append("configurationType", payload.configurationType);
    formData.append("checkInTime", payload.checkInTime);
    formData.append("checkOutTime", payload.checkOutTime);
    formData.append("addOnRequired", payload.addOnRequired ? 1 : 0);
    formData.append("type", payload.type || "permanent");
    formData.append("capacity", payload.capacity);
    formData.append("allowAdditionalGuests", payload.allowAdditionalGuests ? 1 : 0);
    formData.append("description", payload.description || "");
    formData.append("remark", payload.remark || "");

    formData.append("requiresPayment", payload.requiresPayment ? 1 : 0);
    formData.append("requiresApproval", payload.requiresApproval ? 1 : 0);
    formData.append("allowPublicBooking", payload.allowPublicBooking ? 1 : 0);

    if (payload.pricing && Array.isArray(payload.pricing)) {
      formData.append("pricing", JSON.stringify(payload.pricing));
    }

    if (payload.addOns && Array.isArray(payload.addOns)) {
      formData.append("addOns", JSON.stringify(payload.addOns));
    }

    if (payload.minLeadTime) {
      formData.append("minLeadTime", JSON.stringify(payload.minLeadTime));
    }

    if (payload.maxLeadTime) {
      formData.append("maxLeadTime", JSON.stringify(payload.maxLeadTime));
    }

    if (payload.operatingHours) {
      formData.append("operatingHours", JSON.stringify(payload.operatingHours));
    }

    if (payload.photos?.length) {
      payload.photos.forEach((photo) => {
        formData.append("photos", photo);
      });
    }

    const res = await api.post(endpoints.lot.store, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return res.data;
  } catch (err) {
    const message =
      err.response?.data?.message || "failed to create lot";

    throw new Error(message);
  }
};

/**
 * Update an existing lot
 */
export const updateLot = async (id, payload) => {
  try {
    const formData = new FormData();

    formData.append("name", payload.name);
    formData.append("location", payload.location || "");
    formData.append("type", payload.type || "permanent");
    formData.append("capacity", payload.capacity);
    formData.append("configurationType", payload.configurationType);
    formData.append("checkInTime", payload.checkInTime);
    formData.append("checkOutTime", payload.checkOutTime);
    formData.append("allowAdditionalGuests", payload.allowAdditionalGuests ? 1 : 0);
    formData.append("description", payload.description || "");
    formData.append("remark", payload.remark || "");
    formData.append("addOnRequired", payload.addOnRequired ? 1 : 0);
    formData.append("requiresPayment", payload.requiresPayment ? 1 : 0);
    formData.append("requiresApproval", payload.requiresApproval ? 1 : 0);
    formData.append("allowPublicBooking", payload.allowPublicBooking ? 1 : 0);

    if (payload.pricing && Array.isArray(payload.pricing)) {
      formData.append("pricing", JSON.stringify(payload.pricing));
    } else {
      formData.append("pricing", JSON.stringify([]));
    }

    if (payload.addOns && Array.isArray(payload.addOns)) {
      formData.append("addOns", JSON.stringify(payload.addOns));
    } else {
      formData.append("addOns", JSON.stringify([]));
    }

    if (payload.minLeadTime !== undefined) {
      formData.append(
        "minLeadTime",
        payload.minLeadTime
          ? JSON.stringify(payload.minLeadTime)
          : ""
      );
    }

    if (payload.maxLeadTime !== undefined) {
      formData.append(
        "maxLeadTime",
        payload.maxLeadTime
          ? JSON.stringify(payload.maxLeadTime)
          : ""
      );
    }

    if (payload.operatingHours && Array.isArray(payload.operatingHours)) {
      formData.append(
        "operatingHours",
        JSON.stringify(payload.operatingHours)
      );
    } else {
      formData.append("operatingHours", JSON.stringify([]));
    }

    if (payload.photos?.length) {
      payload.photos.forEach((photo) => {
        formData.append("photos", photo);
      });
    }

    const res = await api.put(endpoints.lot.detail(id), formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return res.data;
  } catch (err) {
    const message =
      err.response?.data?.message || "failed to update lot";

    throw new Error(message);
  }
};

/**
 * Delete a lot
 */
export const deleteLot = async (id) => {
  try {
    const res = await api.delete(endpoints.lot.detail(id));
    return res.data;
  } catch (err) {
    const message =
      err.response?.data?.message || "failed to delete lot";
    throw new Error(message);
  }
};

export const deleteLotPhoto = async (
  id,
  fileName
) => {
  const { data } = await api.delete(
    endpoints.lot.deletePhoto(id, fileName)
  );

  return data;
};

/**
 * Load selection data for lot create/edit page
 */
export const getLotSelectionData = async () => {
  try {
    const res = await api.get(endpoints.lot.selectionData);
    return res.data;
  } catch (err) {
    const message =
      err.response?.data?.message ||
      "failed to load lot selection data";

    throw new Error(message);
  }
};

export const getLotOptions = async () => {
  try {
    const res = await api.get(endpoints.lot.options);
    return res.data;
  } catch (err) {
    const message =
      err.response?.data?.message ||
      "failed to load lot options";

    throw new Error(message);
  }
};
